import React, {useEffect} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {ActionOrigin, AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {openDraftChatInSidepanel} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {CollectionPageTemplateCollectionNode} from 'notebook/entry-templates/collections/TemplateCollectionTable';
import {
  TemplateCollectionQuery,
  TemplateCollectionQueryVariables,
} from 'notebook/template-collections/graphql/__generated/TemplateCollectionQuery';
import {TEMPLATE_COLLECTION_QUERY} from 'notebook/template-collections/graphql/TemplateCollectionsQueries';
import TemplateCollectionUtil from 'notebook/template-collections/TemplateCollectionUtil';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

import NewTemplateCollectionButton from './NewTemplateCollectionButton';

function TemplateFromComposeLabel(): JSX.Element {
  useEffect(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_TEMPLATE_FROM_COMPOSE, {
      actionOrigin: ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE,
    });
  }, []);
  return (
    <>
      Entry template from Compose
      <BenchlingIcon icon="sparkle" style={{marginLeft: 8}} />
    </>
  );
}

export interface TemplateCollectibleCreateButtonInternalProps extends Pick<
  ButtonProps,
  'text' | 'isDropdown' | 'disabled'
> {
  collection: CollectionPageTemplateCollectionNode;
  // TODO(BNCH-121653): We should be able to make compact required and remove text here
  compact?: boolean;
  // TODO(BNCH-121653) - Currently, the TemplateCollectibleCreateButton is used in both the
  // `TemplateCollectionsSidepanel` and in the `TemplateCollectionTable`. The way that we render it is
  // slightly different but the functionality is identical in both. The `legacyMode` flag here is intended to
  // provide signal for when we are in the `TemplateCollectionTable`. Once we remove
  // `ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE`, we can remove the `legacyMode` flag.
  legacyMode?: boolean;
}

function TemplateCollectibleCreateButtonInternal({
  collection,
  disabled,
  compact,
  legacyMode = true,
  text = 'Create',
  isDropdown = true,
}: TemplateCollectibleCreateButtonInternalProps): JSX.Element {
  const canCreateCollectibles = collection.permissions && collection.permissions.appendable;
  const templateTypes = TemplateCollectionUtil.getTemplateCollectionTypes(collection);
  const {
    buttonStyle,
    icon,
    displayText,
  }: {buttonStyle: ButtonStyle; icon?: BenchlingIconName; displayText?: React.ReactNode} = legacyMode
    ? {buttonStyle: ButtonStyle.PRIMARY, icon: undefined, displayText: text}
    : {
        buttonStyle: compact ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.SECONDARY,
        icon: 'compose' satisfies BenchlingIconName,
        displayText: undefined,
      };

  return (
    <Dropdown
      menu={
        <Menu width={MenuWidth.FIT_CONTENT} dropLeft={legacyMode || !compact}>
          {templateTypes
            .map((templateType) => ({
              ...templateType,
              menuState: templateType.getCreateMenuState(collection),
            }))
            .filter(({menuState}) => menuState.kind !== 'hidden')
            .map(({name, value, createTemplateTypeClickHandler, menuState}) => {
              const isDisabled = menuState.kind === 'disabled';
              return (
                <Option
                  key={value}
                  onSelect={isDisabled ? undefined : createTemplateTypeClickHandler}
                  isDisabled={isDisabled}
                  tooltipTitle={menuState.kind === 'disabled' ? menuState.reason : undefined}
                  dataTestAttrs={{component: 'TemplateCollectionPage', element: 'option', key: name}}
                >
                  {name}
                </Option>
              );
            })}
          {isAgentTypeEnabled(AgentType.DATA_CAPTURE) && (
            <Option
              key="template-from-compose"
              onSelect={() => {
                trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_TEMPLATE_FROM_COMPOSE, {
                  actionOrigin: ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE,
                });

                openDraftChatInSidepanel(AgentType.DATA_CAPTURE);
              }}
              dataTestAttrs={{
                component: 'TemplateCollectionPage',
                element: 'option',
                key: 'Entry template from Compose',
              }}
            >
              <TemplateFromComposeLabel />
            </Option>
          )}
        </Menu>
      }
    >
      <Button
        isDropdown={isDropdown}
        buttonStyle={buttonStyle}
        icon={icon}
        text={displayText}
        size={ButtonSize.SMALL}
        disabled={disabled || !canCreateCollectibles}
        dataTestAttrs={{component: 'TemplateCollectionPage', element: 'button', key: 'create'}}
      />
    </Dropdown>
  );
}

interface TemplateCollectibleCreateButtonProps extends Omit<
  TemplateCollectibleCreateButtonInternalProps,
  'collection'
> {
  collectionId: string;
}

function TemplateCollectibleCreateButton({
  collectionId,
  compact,
  legacyMode,
  isDropdown = true,
  disabled = false,
}: TemplateCollectibleCreateButtonProps): JSX.Element {
  // TODO(BNCH-123598): Remove query once we can pass down the full Collection object
  return (
    <BenchlingQuery<TemplateCollectionQuery, TemplateCollectionQueryVariables>
      query={TEMPLATE_COLLECTION_QUERY}
      variables={{id: collectionId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data) => {
        assert(!isInaccessible(data.templateCollection), 'Expected result schema to be accessible.');
        const collection = data.templateCollection;
        return (
          <TemplateCollectibleCreateButtonInternal
            compact={compact}
            collection={collection}
            isDropdown={isDropdown}
            disabled={disabled}
            legacyMode={legacyMode}
          />
        );
      }}
    </BenchlingQuery>
  );
}

interface TemplateCollectionOrCollectibleCreateButtonProps extends Omit<
  TemplateCollectibleCreateButtonProps,
  'collectionId'
> {
  collectionId?: string;
}

export default function TemplateCollectionOrCollectibleCreateButton(
  props: TemplateCollectionOrCollectibleCreateButtonProps
): JSX.Element {
  const {collectionId} = props;
  if (collectionId !== undefined) {
    return <TemplateCollectibleCreateButton {...props} collectionId={collectionId} />;
  }
  return <NewTemplateCollectionButton {...props} compact={props.compact ?? true} />;
}
